<template>
  <div>
    <b-overlay
      :show="firewallsResponses('firewallsList') && firewallsEmpty"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <div class="d-flex align-items-end pt-2 pb-6">
        <div class="d-flex flex-column">
          <h1 class="h5 mb-2">
            Firewalls
          </h1>
          <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
            <b-breadcrumb-item :to="{ name: 'Dashboard' }">
              <i class="fa fa-home fa-sm mr-2"></i>
              Home
            </b-breadcrumb-item>
            <b-breadcrumb-item active>Settings</b-breadcrumb-item>
            <b-breadcrumb-item active>Firewalls</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
        <div class="ml-auto">
          <b-button
            size="sm"
            v-wave
            variant="primary"
            class="align-middle btn-shadow"
            :disabled="firewallsResponses('manageFirewallModal')"
            @click="unselectFirewall(), showModal('manageFirewallModal')">
            <i class="fa fa-plus fa-sm p-0 pr-sm-2"></i>
            <span class="d-none d-sm-inline">Add New Firewall</span>
          </b-button>
        </div>
      </div>

      <b-card
        no-body
        class="card-custom card-border">

        <b-card-body>
          <b-table
            :items="firewallsList"
            :fields="fields"
            :class="{ 'table-hover' : !firewallsEmpty }"
            stacked="sm"
            head-variant=""
            show-empty
            responsive
            class="mb-0">
            <template #cell(order)="data">
              <span class="small font-weight-light">{{ data.index + 1 }}</span>
            </template>
            <template #cell(edit)="data">
              <b-button
                size="sm"
                v-wave
                variant="outline-primary"
                class="mr-4 align-middle"
                :disabled="firewallsResponses('manageFirewallModal')"
                @click="showModal('manageFirewallModal', data.item)">
                <i class="fa fa-pen fa-sm"></i>
                <span>Edit</span>
              </b-button>
              <b-button
                :id="`deleteButton-${data.item.id}`"
                v-wave
                size="sm"
                variant="outline-primary"
                class="btn-icon"
                :disabled="firewallsResponses('deleteFirewall')"
                @click="selectFirewall(data.item), showDeletePopover(data.item.id)">
                <i class="fa fa-trash fa-sm"></i>
              </b-button>
            </template>
            <template #empty="scope">
              <no-data-found />
            </template>
          </b-table>
        </b-card-body>
      </b-card>
    </b-overlay>

    <b-popover
      v-for="firewall in firewallsList"
      :key="`popover-${firewall.id}`"
      :target="`deleteButton-${firewall.id}`"
      :ref="`popover-${firewall.id}`"
      triggers="manual blur"
      placement="left">
      <p>Are you sure you want to delete «<span class="font-weight-bolder">{{ firewall.name }}</span>» firewall?</p>
      <div class="d-flex w-100">
        <b-button
          @click="hideDeletePopover(firewall.id)"
          variant="outline-primary"
          size="sm">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="ml-4 text-center justify-content-center flex-grow-1"
          @click="confirmDelete(), hideDeletePopover(firewall.id)">
          <i class="fa fa-check fa-sm"></i>
          <span>Yes, delete</span>
        </b-button>
      </div>
    </b-popover>

    <modal-manage-firewall />
  </div>
</template>

<script>
import {
  // actions
  GET_FIREWALLS,
  GET_FIREWALL,
  MANAGE_FIREWALL,
  DELETE_FIREWALL,

  // mutations
  SELECT_FIREWALL,
  UNSELECT_FIREWALL,
  CLEAR_FIREWALLS,
  CLEAR_MANAGE_FIREWALL_ERRORS,
} from '@/core/services/store/firewalls.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import {
  showToast,
  hideModal,
  showDeletePopover,
  hideDeletePopover,
} from '@/core/utils'
import ModalManageFirewall from './components/ModalManageFirewall.vue'

export default {
  name: 'SettingsFirewalls',
  components: {
    ModalManageFirewall
  },
  data() {
    return {
      fields: [
        {
          label: '#',
          key: 'order',
          class: ['align-middle', 'pl-5'],
          thStyle: 'width: 50px;'
        },
        {
          label: 'Name',
          key: 'name',
          class: ['align-middle', 'font-weight-bold'],
          thStyle: 'width: 33%',
        },
        {
          label: 'Code',
          key: 'slug',
          class: ['align-middle'],
        },
        {
          label: 'Used in groups',
          key: 'groups_used',
          class: ['align-middle', 'text-left text-md-right'],
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-md-right', 'text-nowrap', 'pr-5', 'pl-10'],
        }
      ],

      showToast,
      hideModal,
      showDeletePopover,
      hideDeletePopover,
    }
  },
  computed: {
    ...mapGetters([
      'firewallsList',
      'firewallsEmpty',
      'totalFirewalls',
      'selectedFirewall',
      'firewallsModalTitle',
      'manageFirewallErrors',
      'manageFirewallError',
      'firewallsResponses',
    ]),
  },
  created() {
    this.$store.dispatch(GET_FIREWALLS)

    eventBus.$on('showToast/SettingsFirewalls', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  destroyed() {
    this.$store.commit(CLEAR_FIREWALLS)

    eventBus.$off('showToast/SettingsFirewalls')
  },
  methods: {
    dataIsReady(data) {
      let result = []
      for (let i = 0; i < data.length; i++) {
        result.push(Array.isArray(data[i]) || typeof data == 'object')
      }
      return result.every(el => el)
    },

    showModal(type, firewall) {
      let getFirewall = () => {
        if (!firewall) {
          return true
        }
        return this.$store
          .dispatch(GET_FIREWALL, { id: firewall.id })
          .then(data => {
            this.selectFirewall(data)
          })
      }

      let requirements = {
        manageFirewallModal: [ getFirewall ],
      }

      if (requirements.hasOwnProperty(type)) {
        Promise
          .all(requirements[type].map(promiseFn => promiseFn()))
          .then(res => {
            if (this.dataIsReady(res)) {
              eventBus.$emit('openModal', type)
            }
          })
      } else {
        eventBus.$emit('openModal', type)
      }
    },

    submitFirewall() {
      this.$store
        .dispatch(MANAGE_FIREWALL)
        .then(() => {
          this.hideModal('manageFirewallModal')
        })
    },

    selectFirewall(firewall) {
      this.$store.commit(SELECT_FIREWALL, firewall)
    },

    unselectFirewall() {
      this.$store.commit(UNSELECT_FIREWALL)
    },

    clearErrors() {
      this.$store.commit(CLEAR_MANAGE_FIREWALL_ERRORS)
    },

    getValidationState(field) {
      return this.manageFirewallErrors.some(err => err.field == field) ? false : null
    },

    confirmDelete() {
      this.$store.dispatch(DELETE_FIREWALL)
    },
  }
}
</script>
